import { Ref, forwardRef, useState, SyntheticEvent } from "react"
import { Form, Formik, FormikProps } from "formik"
import * as yup from "yup"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { CreateOrEditPriceListFormValues, CurrencyCode, PriceAdjustmentType } from "../../priceListTypes"
import { TabPanel } from "../../../../../../shared/components/tabs/tabPanel"
import { FormCustomerContent } from "./customer/FormCustomerContent"
import { TabsOrder } from "./formConstants"
import { FormTabs } from "./FormTabs"
import { FormGeneralContent } from "./general/FormGeneralContent"
import { FormProductsContent } from "./products/FormProductsContent"
import { FormPOSContent } from "./pos/FormPOSContent"

type CreateOrEditPriceListFormProps = Readonly<{
    initialValues?: CreateOrEditPriceListFormValues | null
    onSubmit: (formData: CreateOrEditPriceListFormValues) => Promise<void>
}>

const defaultInitialValues: CreateOrEditPriceListFormValues = {
    name: "",
    messageToCustomers: "",
    internalNote: "",
    currencyCode: CurrencyCode.DKK,
    vatCodeId: "",
    priceAdjustmentType: PriceAdjustmentType.NONE,
    percentage: "",
    isActive: true,
    shops: [],
    isDefaultPOSPriceList: false,
    cardSubscriptions: [],
    customers: [],
    products: [],
}

type FormikRef = Ref<FormikProps<CreateOrEditPriceListFormValues>>

export const CreateUpdatePriceListForm = forwardRef(
    ({ initialValues, onSubmit }: CreateOrEditPriceListFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const [value, setValue] = useState(TabsOrder.GENERAL)

        const onTabChange = (_event: SyntheticEvent, newValue: number) => {
            setValue(newValue)
        }

        const validationSchema = yup.object({
            name: yup.string().required(l10n.getString("create-price-list-required-name")),
            shops: yup.array().min(1, l10n.getString("create-price-list-required-shops")),
            messageToCustomers: yup.string(),
            internalNote: yup.string(),
            vatCodeId: yup.string(),
            priceAdjustmentType: yup.string().required(l10n.getString("create-price-list-required-price-adjustment")),
            currencyCode: yup.string().required(l10n.getString("create-price-list-required-currency-code")),
            percentage: yup.number().when("priceAdjustmentType", {
                is: (val: PriceAdjustmentType) => val !== PriceAdjustmentType.NONE,
                then: (schema) =>
                    schema
                        .required(l10n.getString("create-price-list-required-percentage"))
                        .max(100, l10n.getString("create-price-list-max-percentage")),
                otherwise: (schema) => schema.optional(),
            }),
        })

        async function handleSubmit(values: CreateOrEditPriceListFormValues) {
            await onSubmit(values)
        }

        return (
            <Formik<CreateOrEditPriceListFormValues>
                enableReinitialize
                initialValues={initialValues ? initialValues : defaultInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <FormTabs value={value} onChange={onTabChange} />

                    <TabPanel value={value} index={TabsOrder.GENERAL}>
                        <FormGeneralContent />
                    </TabPanel>
                    <TabPanel value={value} index={TabsOrder.CUSTOMER}>
                        <FormCustomerContent />
                    </TabPanel>
                    <TabPanel value={value} index={TabsOrder.PRODUCTS}>
                        <FormProductsContent />
                    </TabPanel>

                    <TabPanel value={value} index={TabsOrder.POS}>
                        <FormPOSContent />
                    </TabPanel>
                </Form>
            </Formik>
        )
    },
)

